import { useTranslation } from 'react-i18next';
import {
  Clock,
  Code2,
  ListChecks,
  MessageCircle,
  Timer,
  type LucideIcon,
} from 'lucide-react';
import { Card } from '@/components/ui/atoms/Card';
import { Divider } from '@/components/ui/atoms/Divider';
import { IconWrapper } from '@/components/ui/atoms/IconWrapper';
import { Tag } from '@/components/ui/atoms/Tag';
import { Typography } from '@/components/ui/atoms/Typography';
import { InterviewOverview } from '@/models/interview';
import { createGTMTracking, getGTMAttributes } from '@/utils/gtm/gtm-tracking';
import { InterviewPreparationTips } from './InterviewPreparationTips';
import { OverviewIconBadge } from './OverviewIconBadge';

interface InterviewOverviewPanelProps {
  overview?: InterviewOverview | null;
  origin: string;
}

interface StepCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  pills?: string[];
  durationLabel?: string | null;
}

/** Pattern B: icon beside title, description, badges, and duration in one text column. */
const StepCard = ({ icon, title, description, pills = [], durationLabel }: StepCardProps) => (
  <Card variant="custom" padding="md" rounded="xl" className="!m-0 h-full border border-light-lila/60 bg-light">
    <div className="flex items-start gap-3">
      <OverviewIconBadge icon={icon} />
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Typography as="h4" variant="h5" color="primary">
            {title}
          </Typography>
          <Typography as="p" variant="body-sm" color="secondary" className="leading-relaxed">
            {description}
          </Typography>
        </div>
        {pills.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {pills.map((pill) => (
              <Tag key={pill} intent="secondary" variant="subtle" size="md">
                {pill}
              </Tag>
            ))}
          </div>
        )}
        {durationLabel ? (
          <>
            <Divider />
            <div className="flex items-center gap-2">
              <IconWrapper size="xs" color="secondary" ariaHidden>
                <Clock />
              </IconWrapper>
              <Typography as="span" variant="body-xs" color="secondary">
                {durationLabel}
              </Typography>
            </div>
          </>
        ) : null}
      </div>
    </div>
  </Card>
);

export const InterviewOverviewPanel = ({ overview, origin }: InterviewOverviewPanelProps) => {
  const { t } = useTranslation('interview');

  if (!overview) return null;

  const {
    duration_minutes,
    spoken_question_count,
    coding_question_count,
    questionnaire_count,
    questionnaire_minutes,
    technical_spoken_count,
    technical_coding_count,
    technical_minutes,
    soft_skills_question_count,
    soft_skills_minutes,
  } = overview;

  const hasTechnical = technical_spoken_count + technical_coding_count > 0;
  const hasSoftSkills = soft_skills_question_count > 0;
  const hasQuestionnaires = questionnaire_count > 0;
  const hasTimedQuestions = spoken_question_count + coding_question_count > 0;
  const hasSteps = hasTechnical || hasSoftSkills || hasQuestionnaires;

  const technicalPills: string[] = [];
  if (technical_spoken_count > 0) {
    technicalPills.push(t('overview.spoken', { count: technical_spoken_count }));
  }
  if (technical_coding_count > 0) {
    technicalPills.push(t('overview.coding', { count: technical_coding_count }));
  }

  const softPills = [t('overview.question_count', { count: soft_skills_question_count })];
  const questionnairePills = [t('overview.questionnaires', { count: questionnaire_count })];

  return (
    <Card
      variant="outlined"
      padding="lg"
      rounded="xl"
      className="!m-0 w-full flex flex-col gap-5 md:gap-6 !border-light-lila/60"
      {...getGTMAttributes(createGTMTracking('VIEW_AI_TEST_OVERVIEW', { origin }))}
    >
      <Typography as="h3" variant="h2" color="primary">
        {t('overview.title')}
      </Typography>

      {(duration_minutes || hasTimedQuestions) && (
        <div
          className={`grid grid-cols-1 gap-4 items-stretch ${
            duration_minutes && hasTimedQuestions ? 'md:grid-cols-3' : ''
          }`}
        >
          {duration_minutes ? (
            <Card
              variant="custom"
              padding="md"
              rounded="xl"
              className={`!m-0 h-full border border-light-lila/60 bg-light ${
                duration_minutes && hasTimedQuestions ? 'md:col-span-1' : ''
              }`}
            >
              <div className="flex h-full items-center gap-3">
                <OverviewIconBadge icon={Clock} size="md" className="self-start mt-2" />
                <div className="flex min-w-0 flex-col gap-3">
                  <Typography as="span" variant="caption" color="secondary">
                    {t('overview.stats.duration_label')}
                  </Typography>
                  <Typography as="p" variant="h3" color="primary" weight="bold">
                    {t('overview.stats.duration_value', { count: duration_minutes })}
                  </Typography>
                </div>
              </div>
            </Card>
          ) : null}

          {hasTimedQuestions && (
            <Card
              variant="custom"
              padding="md"
              rounded="xl"
              className={`!m-0 h-full border border-light-lila/60 bg-light ${
                duration_minutes && hasTimedQuestions ? 'md:col-span-2' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <OverviewIconBadge icon={Timer} />
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <Typography as="h4" variant="h5" color="primary">
                    {t('overview.time_limits_title')}
                  </Typography>
                  <Typography as="p" variant="body-sm" color="secondary" className="leading-relaxed">
                    {t('overview.time_limits')}
                  </Typography>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {hasSteps && (
        <div className="flex flex-col gap-4 md:gap-5">
          <Typography as="h4" variant="h4" color="primary">
            {t('overview.steps_title')}
          </Typography>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
            {hasTechnical && (
              <StepCard
                icon={Code2}
                title={t('overview.technical_title')}
                description={t('overview.technical_desc')}
                pills={technicalPills}
                durationLabel={
                  technical_minutes
                    ? t('overview.duration', { count: technical_minutes })
                    : null
                }
              />
            )}
            {hasSoftSkills && (
              <StepCard
                icon={MessageCircle}
                title={t('overview.soft_skills_title')}
                description={t('overview.soft_skills_desc')}
                pills={softPills}
                durationLabel={
                  soft_skills_minutes
                    ? t('overview.duration', { count: soft_skills_minutes })
                    : null
                }
              />
            )}
            {hasQuestionnaires && (
              <div className={hasTechnical || hasSoftSkills ? 'md:col-span-2' : ''}>
                <StepCard
                  icon={ListChecks}
                  title={t('overview.psychometric_title')}
                  description={t('overview.psychometric_desc')}
                  pills={questionnairePills}
                  durationLabel={
                    questionnaire_minutes
                      ? t('overview.duration', { count: questionnaire_minutes })
                      : null
                  }
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4 md:gap-5">
        <Typography as="h4" variant="h4" color="primary">
          {t('prep_tips.section_title')}
        </Typography>
        <InterviewPreparationTips />
      </div>
    </Card>
  );
};
